import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

const RecipeTree = ({ familyTree, currentRecipeId, currentPage, currentImageUrl, className }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(new Set());

  const handleClick = useCallback(
    (id) => {
      console.log('RecipeTree handleClick called with ID:', id);
      console.log('Current page:', currentPage);
      
      const params = new URLSearchParams({ lastPageType: currentPage.type });
      if (currentPage.recipeId) params.set('lastRecipeId', currentPage.recipeId);
      
      const targetUrl = `/recette/${id}?${params.toString()}`;
      console.log('Navigating to:', targetUrl);
      
      navigate(targetUrl);
    },
    [navigate, currentPage]
  );

  useEffect(() => {
    if (!currentRecipeId) return;
    const expandParents = (node) => {
      if (!node.children) return false;
      for (const child of node.children) {
        if (child.id === currentRecipeId || expandParents(child)) {
          setExpanded((prev) => new Set(prev).add(node.id));
          return true;
        }
      }
      return false;
    };
    expandParents(familyTree);
  }, [currentRecipeId, familyTree]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Recherche récursive d'un nœud par id
  const findNodeById = (node, id) => {
    if (!node) return null;
    if (node.id === id) return node;
    if (!node.children) return null;
    for (const child of node.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
    return null;
  };

  const renderNode = (node, depth = 0) => {
    const hasKids = node.children?.length > 0;
    const isOpen = expanded.has(node.id);
    const isActive = node.id === currentRecipeId;
    const date = format(new Date(node.generatedAt), "dd/MM/yyyy 'à' HH:mm", { locale: fr });
    const recipeUrlFound = (node.imgUrlArray || []).find(url => url.indexOf('/recipe') > -1);
    const suggestionUrlFound = (node.imgUrlArray || []).find(url => url.indexOf('/suggestion') > -1);
    const imageSrc = (isActive && currentImageUrl)
      ? currentImageUrl
      : (recipeUrlFound || suggestionUrlFound || 'https://via.placeholder.com/64?text=Recette');
    
    return (
      <div key={node.id} className="flex flex-col" style={{ marginLeft: depth * 16 }}>
                    {isActive && (
              <div className="mb-2">
              <span
                className="text-xs px-3 py-1 rounded-full text-white font-medium shadow-sm"
                style={{ backgroundColor: '#87A96B' }}
              >
                Version actuelle
              </span>
              </div>
            )}
        <div
          className={`group flex flex-col md:flex-row items-center p-4 mb-3 rounded-2xl border transition-all cursor-pointer select-none ${
            isActive 
              ? 'shadow-lg bg-green-50 border-2' 
              : 'border-gray-200 bg-white hover:shadow-lg'
          }`}
          style={{
            ...(isActive ? { borderColor: '#87A96B' } : {}),
            pointerEvents: 'auto',
            userSelect: 'none'
          }}
          onClick={(e) => {
            console.log('Click event triggered on RecipeTree item:', node.id);
            console.log('Event target:', e.target);
            console.log('Is active:', isActive);
            
            // Empêcher la navigation si on clique sur le bouton d'expansion/collapse
            if (e.target.closest('button')) {
              console.log('Click on button detected, stopping propagation');
              e.stopPropagation();
              return;
            }
            
            // Naviguer vers la recette (même si c'est la version actuelle pour permettre le rafraîchissement)
            console.log('Calling handleClick with:', node.id);
            handleClick(node.id);
          }}
          onMouseEnter={(e) => {
            if (!isActive) {
              e.currentTarget.style.borderColor = '#87A96B';
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              e.currentTarget.style.borderColor = '#e5e7eb';
            }
            }}
          >
            
            {hasKids && (
            <button 
              onClick={() => toggle(node.id)} 
              className="self-end md:self-auto mb-2 md:mb-0 md:mr-3 p-1 rounded-full hover:bg-gray-100 transition-colors"
              style={{ color: '#87A96B' }}
            >
              {isOpen ? (
              <ChevronDown size={20} />
              ) : (
              <ChevronRight size={20} />
              )}
            </button>
            )}
            
            <div className="flex flex-col items-center md:items-start">

            <img
              src={imageSrc}
              alt={node.title}
              className="w-16 h-16 rounded-xl mb-3 md:mb-0 md:mr-4 object-cover transition-transform group-hover:scale-105 shadow-sm"
            />
            </div>

            <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start font-bold" style={{ color: '#2C3E50' }}>
              {node.title}

            </div>
            <div 
              className="text-sm mt-1" 
              style={{ color: '#6c757d' }} 
              title={format(new Date(node.generatedAt), 'PPPP p', { locale: fr })}
            >
              Générée le {date}
            </div>
            </div>

            <ExternalLink 
            className="mt-2 md:mt-0 md:ml-2 opacity-0 group-hover:opacity-100 transition-opacity" 
            size={18} 
            style={{ color: '#E67E22' }}
            />
          </div>

          {hasKids && isOpen && (
            <div 
            className="border-l-2 relative"
            style={{ borderColor: '#87A96B' }}
            >
            {/* Ligne de connexion décorative */}
            <div 
              className="absolute top-0 left-0 w-4 h-6 border-b-2 border-l-2 rounded-bl-lg"
              style={{ borderColor: '#87A96B' }}
            ></div>
            {node.children
              .filter((child) => child.id !== currentRecipeId)
              .map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`p-6 bg-white rounded-3xl shadow-lg border border-gray-100 ${className || ''}`}>
      <div className="flex items-center mb-4">
        <div 
          className="w-3 h-3 rounded-full mr-3"
          style={{ backgroundColor: '#87A96B' }}
        ></div>
        <h3 className="text-lg font-semibold" style={{ color: '#2C3E50' }}>
          Historique des versions
        </h3>

      </div>
      {/* Version actuelle en premier */}
      {currentRecipeId && findNodeById(familyTree, currentRecipeId) && (
        <div className="mb-4">
          {renderNode(findNodeById(familyTree, currentRecipeId), 0)}
        </div>
      )}
      {/* Puis le reste de l'arbre (sauf la version actuelle si différente de la racine) */}
      {(!currentRecipeId || currentRecipeId !== familyTree?.id) && renderNode(familyTree)}
    </div>
  );
};

export default RecipeTree;
